import MediaQuery from 'react-responsive';
import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';

import ColoredStrategyDescription from '#/components/UnifiedRfqs/components/strategy-description/ColoredStrategyDescription';
import {
  TradeTapeItem,
  isRejectedTrade,
} from '@paradigm/features/src/unified-rfqs/entities/drfq/trades';
import { getOppositeSide } from '@paradigm/features/src/unified-rfqs/ui/rfqs/quotes-functions';
import {
  Container,
  DateInfo,
  getSideText,
  TradeInfoGrid,
  Label,
  TagSpan,
} from '#/components/UnifiedRfqs/components/DetailsModal';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { useFormattedDrfqMakerPrice } from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import { DrfqAccount } from './DrfqAccount';

interface DrfqTradeDescription {
  trade: TradeTapeItem;
  viewerRole: Drfq['role'];
  drfq?: Drfq | null | undefined;
  counterpartyDesk?: string | undefined | null;
}

export default function TradeDescription({
  trade,
  drfq,
  viewerRole: role,
  counterpartyDesk,
}: DrfqTradeDescription) {
  const formattedPrice = useFormattedDrfqMakerPrice({
    price: trade.price,
    side: trade.side,
    drfqInfo: {
      ...trade,
      role: 'TAKER',
      product_codes: trade.legs.map((leg) => leg.product_code),
    },
  });

  return (
    <Container aria-label="trade description">
      <MediaQuery minWidth={MOBILE_BREAKPOINT}>
        <DrfqAccount drfq={drfq} />
      </MediaQuery>
      <TradeInfoGrid>
        <ActionLabel
          trade={trade}
          counterpartyDesk={counterpartyDesk}
          viewerRole={role}
        />
        <MediaQuery minWidth={MOBILE_BREAKPOINT}>
          <TagSpan>
            <ColoredStrategyDescription>
              {trade.description}
            </ColoredStrategyDescription>
          </TagSpan>
        </MediaQuery>
        <Label>at</Label>
        <div css="text-align: left;">
          <TagSpan>{formattedPrice}</TagSpan>
          <span>{trade.quote_currency}</span>
        </div>
      </TradeInfoGrid>
      <MediaQuery minWidth={MOBILE_BREAKPOINT}>
        <DateInfo>
          <p>{hourFormat(trade.filled_at)}</p>
          <p>{shortDateFormat(trade.filled_at)}</p>
        </DateInfo>
      </MediaQuery>
    </Container>
  );
}

interface ActionLabelProps {
  trade: TradeTapeItem;
  viewerRole: Drfq['role'];
  counterpartyDesk?: string | undefined | null;
}

function ActionLabel({
  trade,
  counterpartyDesk,
  viewerRole: role,
}: ActionLabelProps) {
  if (isRejectedTrade(trade)) return null;

  const oppositeSide = getOppositeSide(trade.side);
  if (counterpartyDesk != null)
    return (
      <TradeInfoGrid>
        <TagSpan css="align-self: start;">{counterpartyDesk}</TagSpan>
        {getSideText(oppositeSide)}
      </TradeInfoGrid>
    );

  const side = role === 'MAKER' ? oppositeSide : trade.side;

  return getSideText(side);
}
